
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import DataLinkButton from '@/components/ui/data-link-button';
import DataTableButton from '@/components/ui/data-table-button';
import { calculatePortfolioValue, generateCashFlow, calculateNPV, calculateROIMetrics } from '@/lib/financial';
import { DataSource } from '@/components/data-tables/DataTables';
import { DashboardMetricsCards } from './DashboardMetricsCards';
import { DashboardQuickActions } from './DashboardQuickActions';
import { DashboardProgress } from './DashboardProgress';

interface DashboardOverviewProps {
  onNavigateToAIDashboard?: () => void;
}

// Mock data for demonstration
const mockTenements = [
  {
    id: '1',
    name: 'Goldmine Project A',
    status: 'exploration',
    resourceEstimation: {
      marketValue: 5000000,
      mineralType: 'gold',
      classification: 'indicated'
    }
  },
  {
    id: '2', 
    name: 'Copper Prospect B',
    status: 'development',
    resourceEstimation: {
      marketValue: 3000000,
      mineralType: 'copper',
      classification: 'measured'
    }
  }
];

export function DashboardOverview({ onNavigateToAIDashboard }: DashboardOverviewProps) {
  const { user } = useAuth();
  const [balanceSheetData, setBalanceSheetData] = useState<any[]>([]);
  const [financialAssumptions, setFinancialAssumptions] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadFinancialData();
    }
  }, [user]);

  const loadFinancialData = async () => {
    if (!user) return;

    try {
      const [balanceSheetResponse, assumptionsResponse] = await Promise.all([
        supabase
          .from('balance_sheet_entries')
          .select('*')
          .eq('user_id', user.id)
          .order('period_end_date', { ascending: false })
          .limit(10),
        supabase
          .from('financial_assumptions')
          .select('assumptions_data')
          .eq('user_id', user.id)
          .maybeSingle()
      ]);

      if (balanceSheetResponse.error) throw balanceSheetResponse.error;
      if (assumptionsResponse.error) throw assumptionsResponse.error;

      setBalanceSheetData(balanceSheetResponse.data || []);
      setFinancialAssumptions(assumptionsResponse.data?.assumptions_data || null);
    } catch (error) {
      console.error('Error loading financial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate key metrics
  const portfolioValue = calculatePortfolioValue(mockTenements);
  const sampleCashFlow = generateCashFlow({
    initialInvestment: 1000000,
    annualRevenue: 500000,
    annualCosts: 200000,
    growthRate: 5,
    discountRate: 10,
    years: 5
  });
  const npv = calculateNPV(sampleCashFlow);
  const roiMetrics = calculateROIMetrics(sampleCashFlow, 50000, 0.2);

  const totalAssets = balanceSheetData
    .filter(entry => entry.account_category === 'Assets')
    .reduce((sum, entry) => sum + Number(entry.balance), 0);

  const totalLiabilities = balanceSheetData
    .filter(entry => entry.account_category === 'Liabilities')
    .reduce((sum, entry) => sum + Number(entry.balance), 0);

  const netWorth = totalAssets - totalLiabilities;

  // Check data availability for AI model
  const hasBalanceSheetData = balanceSheetData.length > 0;
  const hasFinancialAssumptions = financialAssumptions !== null;
  const canRunModel = hasBalanceSheetData || hasFinancialAssumptions;

  const handleModelGenerated = () => {
    console.log('Financial model generated successfully');
    if (onNavigateToAIDashboard) {
      onNavigateToAIDashboard();
    }
  };

  const handleViewBalanceSheet = (dataSource: DataSource) => {
    console.log('Viewing balance sheet data:', dataSource);
  };

  const handleViewDataTable = () => {
    console.log('Viewing balance sheet data table');
  };

  const handleViewAIDashboard = () => {
    if (onNavigateToAIDashboard) {
      onNavigateToAIDashboard();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Dashboard Overview</h1>
          <p className="text-gray-600">Welcome back! Here's your financial summary.</p>
        </div>
        <div className="flex gap-2">
          <DataLinkButton 
            dataSource={("balance-sheet" as unknown) as DataSource}
            onViewData={handleViewBalanceSheet}
            label="View Balance Sheet"
          />
          <DataTableButton 
            onClick={handleViewDataTable}
            label="Balance Sheet Data"
          />
        </div>
      </div>

      {/* Key Metrics Cards */}
      <DashboardMetricsCards
        netWorth={netWorth}
        portfolioValue={portfolioValue}
        npv={npv}
        roiMetrics={roiMetrics}
      />

      {/* Quick Actions */}
      <DashboardQuickActions
        hasBalanceSheetData={hasBalanceSheetData}
        hasFinancialAssumptions={hasFinancialAssumptions}
        canRunModel={canRunModel}
        onModelGenerated={handleModelGenerated}
        onViewAIDashboard={handleViewAIDashboard}
      />

      {/* Progress Indicators */}
      <DashboardProgress
        hasBalanceSheetData={hasBalanceSheetData}
        hasFinancialAssumptions={hasFinancialAssumptions}
        canRunModel={canRunModel}
      />
    </div>
  );
}
